window.RewardPoints = (function () {
  const API_PATH = '/mem-service/api/v1/bigcommerce/get_product_multiple_point';
  const LIST_API_PATH = '/mem-service/api/v1/bigcommerce/get_product_list_multiple_point';
  const FIRST_ORDER_BONUS = 500;
  const ZERO_DECIMAL_CURRENCIES = [
    'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA', 'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF'
  ];
  const rewardCache = new Map();

  function isJapanSite() {
    return (window.renogyConfig?.country || '').toUpperCase() === 'JP';
  }

  function isLoggedIn() {
    if ((window.renogyConfig?.user_email || '').trim()) {
      return true;
    }

    return Boolean(getAccessToken());
  }

  function hasMountedEmail() {
    return Boolean((window.renogyConfig?.user_email || '').trim());
  }

  function isGuestByEmail() {
    return !hasMountedEmail();
  }

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }

  function getAccessToken() {
    if (window.AccountHome?.getAccessToken) {
      const token = window.AccountHome.getAccessToken();
      if (token) return token;
    }

    const appEnv = window.renogyConfig?.mode;
    if (!appEnv) return null;

    const accessTokenKey = `renogy_account_access_token_key_${appEnv}_1.0`;
    return getCookie(accessTokenKey);
  }

  function buildRequestHeaders(includeToken = true) {
    const headers = {
      'Content-Type': 'application/json'
    };

    if (!includeToken || !hasMountedEmail()) {
      return headers;
    }

    const token = getAccessToken();
    if (!token) {
      return null;
    }

    headers['X-Token'] = token;
    return headers;
  }

  function parseApiPayload(data) {
    return data?.data ?? data?.result ?? data;
  }

  function isApiSuccess(response, data) {
    if (!response.ok) return false;
    if (data?.code && data.code !== '000000') return false;
    return true;
  }

  function isTokenExpired(data) {
    return data?.code === 'SEC003' || data?.msg === 'TOKEN_EXPIRED';
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function getActiveCurrency() {
    return (window.Shopify?.currency?.active || window.renogyConfig?.currency || '').toUpperCase();
  }

  function isZeroDecimalCurrency(currency) {
    return ZERO_DECIMAL_CURRENCIES.includes((currency || getActiveCurrency()).toUpperCase());
  }

  function normalizeCartUnitPrice(finalPrice) {
    const raw = Number(finalPrice) || 0;
    if (!raw) return 0;

    if (isZeroDecimalCurrency()) {
      return Math.floor(raw / 100);
    }

    return raw;
  }

  function getUnitPriceBase(unitPrice) {
    const raw = Number(unitPrice) || 0;
    if (!raw) return 0;

    if (isJapanSite()) {
      return Math.floor(raw);
    }

    return Math.floor(raw / 100);
  }

  function calculateTotalMiles(totalPrice) {
    const price = Number(totalPrice) || 0;
    if (!price) return 0;

    if (isJapanSite()) {
      return Math.floor(price / 100);
    }

    return Math.floor(price / 100);
  }

  function calculateItemMiles(unitPriceBase, quantity) {
    const base = Number(unitPriceBase) || 0;
    const qty = Number(quantity) || 0;
    if (!base || !qty) return 0;

    if (isJapanSite()) {
      return base * qty * 0.01;
    }

    return base * qty;
  }

  function calculateItemRays(unitPriceBase, quantity, multiple) {
    const base = Number(unitPriceBase) || 0;
    const qty = Number(quantity) || 0;
    const times = Number(multiple);
    if (!base || !qty || !Number.isFinite(times)) return 0;

    const siteFactor = isJapanSite() ? 0.1 : 10;
    return Math.floor(base * qty * times * siteFactor);
  }

  function calculateLineRays(linePrice, times) {
    const price = Number(linePrice) || 0;
    const multiple = Number(times);
    if (!price || !Number.isFinite(multiple)) return 0;

    const siteFactor = isJapanSite() ? 0.1 : 10;

    if (isJapanSite()) {
      return Math.floor(price * multiple * siteFactor);
    }

    return Math.floor(Math.floor(price / 100) * multiple * siteFactor);
  }

  function canAddMilesFirstBonus(payload) {
    if (payload?.firstOrderMilesGranted === true) {
      return false;
    }

    if (payload?.firstOrderMilesGranted === false) {
      return true;
    }

    return Boolean(payload?.meilFirst);
  }

  function canAddRaysFirstBonus(payload) {
    if (payload?.firstOrderBonusPointsGranted === true) {
      return false;
    }

    if (payload?.firstOrderBonusPointsGranted === false) {
      return true;
    }

    return Boolean(payload?.raysFirst);
  }

  function parseRewardResponse(data, response, loggedIn, usedToken) {
    const treatAsAuth = loggedIn && usedToken;

    if (treatAsAuth && !isApiSuccess(response, data)) {
      if (isTokenExpired(data)) {
        return { tokenExpired: true };
      }

      return { error: true };
    }

    const payload = parseApiPayload(data);
    const multiple = Number(payload?.multiple);
    const times = Number.isFinite(multiple) ? multiple : null;

    if (treatAsAuth && times === null) {
      return { error: true };
    }

    return {
      error: false,
      times,
      meilFirst: treatAsAuth ? canAddMilesFirstBonus(payload) : false,
      raysFirst: treatAsAuth ? canAddRaysFirstBonus(payload) : false
    };
  }

  async function fetchProductReward(variantId, productId) {
    const authByEmail = hasMountedEmail();
    const cacheKey = `${variantId || productId}:${authByEmail ? 'auth' : 'guest'}`;
    if (rewardCache.has(cacheKey)) {
      return rewardCache.get(cacheKey);
    }

    const apiUrl = window.renogyConfig?.apiUrl;
    if (!apiUrl) {
      const result = { error: authByEmail };
      rewardCache.set(cacheKey, result);
      return result;
    }

    const params = new URLSearchParams({
      productId: variantId || productId,
      platformId: window.renogyConfig?.storeId || ''
    });
    const url = `${apiUrl}${API_PATH}?${params.toString()}`;

    async function doFetch(includeToken) {
      const headers = buildRequestHeaders(includeToken) || {
        'Content-Type': 'application/json'
      };

      const response = await fetch(url, { method: 'GET', headers });
      let data = null;

      try {
        data = await response.json();
      } catch (error) {
        return { error: authByEmail && includeToken };
      }

      return parseRewardResponse(data, response, authByEmail, includeToken);
    }

    try {
      let includeToken = authByEmail;
      let result = await doFetch(includeToken);

      if (result.tokenExpired) {
        await sleep(3000);
        result = await doFetch(includeToken);

        if (result.tokenExpired) {
          includeToken = false;
          result = await doFetch(includeToken);
        }
      }

      if (result.tokenExpired) {
        result = { error: authByEmail };
      }

      rewardCache.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Failed to fetch reward data:', error);
      const result = { error: authByEmail };
      rewardCache.set(cacheKey, result);
      return result;
    }
  }

  function getSensorsClient() {
    try {
      if (typeof sensors !== 'undefined' && sensors && typeof sensors.track === 'function') {
        return sensors;
      }
    } catch (error) {}

    const client = window.sensorsDataAnalytic201505 || window['sensorsDataAnalytic201505'];
    if (client && typeof client.track === 'function') {
      return client;
    }

    return null;
  }

  const trackDedupeCache = new Map();

  function trackSensorsEvent(cacheKey, eventName, properties) {
    try {
      const client = getSensorsClient();
      if (!client) return;

      const signature = JSON.stringify(properties);
      if (trackDedupeCache.get(cacheKey) === signature) {
        return;
      }

      trackDedupeCache.set(cacheKey, signature);
      client.track(eventName, properties);
    } catch (error) {
      console.error(`Failed to track ${eventName}:`, error);
    }
  }

  function trackMembershipCartRewardView(properties) {
    trackSensorsEvent('membershipCartRewardView', 'membershipCartRewardView', properties);
  }

  function trackMembershipPdpRewardView(properties) {
    trackSensorsEvent('MembershipPdpRewardView', 'MembershipPdpRewardView', properties);
  }

  async function calculateProductRewards(price, variantId, productId) {
    const linePrice = Number(price) || 0;
    if (!linePrice) {
      return { hide: true };
    }

    const result = await fetchProductReward(variantId, productId);
    let rewards;

    if (!isGuestByEmail()) {
      if (result.error) {
        return { hide: true };
      }

      let miles = calculateTotalMiles(linePrice);
      let rays = result.times !== null ? calculateLineRays(linePrice, result.times) : 0;
      const hasRaysBase = result.times !== null;

      if (result.meilFirst) {
        miles += FIRST_ORDER_BONUS;
      }

      if (result.raysFirst) {
        rays += FIRST_ORDER_BONUS;
      }

      rewards = {
        hide: false,
        showGuestHint: false,
        miles,
        rays,
        hasMiles: miles > 0,
        hasRays: (hasRaysBase || result.raysFirst) && rays > 0,
        firstOrderBonusShown: Boolean(result.meilFirst || result.raysFirst)
      };
    } else {
      let miles = calculateTotalMiles(linePrice) + FIRST_ORDER_BONUS;
      let rays = null;
      let hasRays = false;

      if (!result.error && result.times !== null) {
        hasRays = true;
        rays = calculateLineRays(linePrice, result.times) + FIRST_ORDER_BONUS;
      }

      rewards = {
        hide: false,
        showGuestHint: isGuestByEmail(),
        miles,
        rays,
        hasMiles: miles > 0,
        hasRays: hasRays && rays > 0,
        firstOrderBonusShown: true
      };
    }

    if (!rewards.hide && (rewards.hasMiles || rewards.hasRays)) {
      trackMembershipPdpRewardView({
        product_id: productId || variantId || '',
        estimated_miles: rewards.miles,
        estimated_rays: rewards.rays ?? 0
      });
    }

    return rewards;
  }

  function canAddRaysFirstBonusFromList(payload, guest) {
    if (guest) return true;
    return payload?.firstOrderBonusPointsGranted !== true;
  }

  function canAddMilesFirstBonusFromList(payload, guest) {
    if (guest) return true;
    return payload?.firstOrderMilesGranted !== true;
  }

  function parseListRewardResponse(data, response, guest) {
    if (!isApiSuccess(response, data)) {
      return { error: guest ? false : true };
    }

    const payload = parseApiPayload(data);
    const multipleMap = new Map();

    (payload?.contentList || []).forEach((item) => {
      if (item?.productId == null) return;
      const multiple = Number(item.multiple);
      if (!Number.isFinite(multiple)) return;
      multipleMap.set(String(item.productId), multiple);
    });

    return {
      error: false,
      multipleMap,
      raysFirst: canAddRaysFirstBonusFromList(payload, guest),
      meilFirst: canAddMilesFirstBonusFromList(payload, guest)
    };
  }

  async function fetchProductListReward(items) {
    const guest = isGuestByEmail();
    const email = (window.renogyConfig?.user_email || '').trim();
    const productIds = [
      ...new Set(
        items
          .map((item) => item.variantId || item.productId)
          .filter((id) => id !== null && id !== undefined && id !== '')
      )
    ];

    if (!productIds.length) {
      return { error: false, multipleMap: new Map(), raysFirst: guest, meilFirst: guest };
    }

    const cacheKey = `list:${productIds.sort().join(',')}:${email || 'guest'}`;
    if (rewardCache.has(cacheKey)) {
      return rewardCache.get(cacheKey);
    }

    const apiUrl = window.renogyConfig?.apiUrl;
    if (!apiUrl) {
      const result = { error: !guest, multipleMap: new Map(), raysFirst: guest, meilFirst: guest };
      rewardCache.set(cacheKey, result);
      return result;
    }

    const params = new URLSearchParams({
      platformId: window.renogyConfig?.storeId || ''
    });
    productIds.forEach((id) => params.append('productIds', id));
    if (email) {
      params.append('email', email);
    }

    const url = `${apiUrl}${LIST_API_PATH}?${params.toString()}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      let data = null;

      try {
        data = await response.json();
      } catch (error) {
        const result = { error: !guest, multipleMap: new Map(), raysFirst: guest, meilFirst: guest };
        rewardCache.set(cacheKey, result);
        return result;
      }

      const result = parseListRewardResponse(data, response, guest);
      rewardCache.set(cacheKey, result);
      return result;
    } catch (error) {
      console.error('Failed to fetch cart reward list data:', error);
      const result = { error: !guest, multipleMap: new Map(), raysFirst: guest, meilFirst: guest };
      rewardCache.set(cacheKey, result);
      return result;
    }
  }

  function getCartItemMultiple(multipleMap, item) {
    const variantKey = String(item.variantId || '');
    const productKey = String(item.productId || '');

    if (variantKey && multipleMap.has(variantKey)) {
      return multipleMap.get(variantKey);
    }

    if (productKey && multipleMap.has(productKey)) {
      return multipleMap.get(productKey);
    }

    return null;
  }

  async function calculateCartRewards(data) {
    if (!data.items.length) {
      return { hide: true };
    }

    const guest = isGuestByEmail();
    const listResult = await fetchProductListReward(data.items);

    if (listResult.error) {
      return { hide: true };
    }

    let rays = 0;
    let hasRays = false;
    let miles = 0;

    data.items.forEach((item) => {
      const unitPrice = item.unitPrice ?? item.linePrice ?? 0;
      const base = getUnitPriceBase(unitPrice);
      const quantity = Number(item.quantity) || 1;

      miles += calculateItemMiles(base, quantity);

      const multiple = getCartItemMultiple(listResult.multipleMap, item);
      if (multiple === null) return;

      hasRays = true;
      rays += calculateItemRays(base, quantity, multiple);
    });

    miles = Math.floor(miles);

    if (listResult.meilFirst) {
      miles += FIRST_ORDER_BONUS;
    }

    if (listResult.raysFirst && (!guest || hasRays)) {
      rays += FIRST_ORDER_BONUS;
    }

    const rewards = {
      hide: false,
      showGuestHint: guest,
      miles,
      rays: hasRays || listResult.raysFirst ? rays : null,
      hasMiles: miles > 0,
      hasRays: (hasRays || listResult.raysFirst) && rays > 0,
      firstOrderBonusShown: Boolean(listResult.meilFirst || listResult.raysFirst)
    };

    if (!rewards.hide && (rewards.hasMiles || rewards.hasRays)) {
      trackMembershipCartRewardView({
        cart_value: data.totalPrice,
        miles: rewards.miles,
        rays: rewards.rays ?? 0,
        first_order_bonus_shown: Boolean(rewards.firstOrderBonusShown)
      });
    }

    return rewards;
  }

  const GUEST_HINT_EM_CLASS = 'rg-cart-drawer-reward__guest-hint-em';

  function formatGuestHint(template, rewards) {
    if (!template) return '';

    let text = template;

    function applyEmBlock(blockName, placeholder, value, hasValue) {
      const blockRe = new RegExp(`\\[${blockName}\\]([\\s\\S]*?)\\[\\/${blockName}\\]`, 'g');

      if (!hasValue) {
        text = text.replace(
          new RegExp(`\\s*[＋+]\\s*\\[${blockName}\\][\\s\\S]*?\\[\\/${blockName}\\]`, 'g'),
          ''
        );
        text = text.replace(
          new RegExp(`\\[${blockName}\\][\\s\\S]*?\\[\\/${blockName}\\]\\s*[＋+]\\s*`, 'g'),
          ''
        );
        text = text.replace(blockRe, '');
        return;
      }

      text = text.replace(blockRe, (_, inner) => {
        const filled = inner.replace(new RegExp(`\\[${placeholder}\\]`, 'g'), String(value));
        return `<span class="${GUEST_HINT_EM_CLASS}">${filled}</span>`;
      });
    }

    if (text.includes('[miles_em]')) {
      applyEmBlock('miles_em', 'miles', rewards.miles, rewards.hasMiles);
      applyEmBlock('rays_em', 'rays', rewards.rays, rewards.hasRays);
    } else {
      if (rewards.hasMiles) {
        text = text.replace(/\[miles\]/g, String(rewards.miles));
      } else {
        text = text.replace(/\[miles\][^\[]*?(\s*\+\s*|\s*＋\s*)?/g, '');
      }

      if (rewards.hasRays) {
        text = text.replace(/\[rays\]/g, String(rewards.rays));
      } else {
        text = text.replace(/(\s*\+\s*|\s*＋\s*)?\[rays\][^\[]*/g, '');
      }
    }

    return text.replace(/\s{2,}/g, ' ').trim();
  }

  function formatGuestRewardValues(template, rewards) {
    const values = [];

    function addEmBlock(blockName, placeholder, value, hasValue) {
      if (!hasValue) return;

      const blockRe = new RegExp(`\\[${blockName}\\]([\\s\\S]*?)\\[\\/${blockName}\\]`);
      const fallback = placeholder === 'miles' ? `[${placeholder}] Miles` : `[${placeholder}] Rays`;
      const inner = template.match(blockRe)?.[1] || fallback;
      const filled = inner.replace(new RegExp(`\\[${placeholder}\\]`, 'g'), String(value));

      values.push(`<span class="${GUEST_HINT_EM_CLASS}">${filled}</span>`);
    }

    addEmBlock('miles_em', 'miles', rewards.miles, rewards.hasMiles);
    addEmBlock('rays_em', 'rays', rewards.rays, rewards.hasRays);

    return values.join(' + ');
  }

  function decodeHtmlEntities(value) {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = value || '';
    return textarea.value;
  }

  function applyRewardDisplay(root, rewards) {
    if (!root) return;

    const guestHintElement = root.querySelector('[data-reward-guest-hint]');
    const guestRewardsElement = root.querySelector('[data-reward-guest-rewards]');
    const milesElement = root.querySelector('[data-reward-miles]');
    const raysElement = root.querySelector('[data-reward-rays]');
    const separatorElement = root.querySelector('[data-reward-separator]');
    const raysSuffixElement = root.querySelector('[data-reward-rays-suffix]');
    const valueElement = root.querySelector('[data-reward-value]');
    const afterPurchaseElement = root.querySelector('[data-reward-after-purchase]');
    const wrapElement = root.querySelector('[data-reward-wrap]');

    if (rewards.hide || (!rewards.hasMiles && !rewards.hasRays)) {
      root.hidden = true;
      return;
    }

    root.hidden = false;

    const iconEl = root.querySelector('[data-reward-icon]');
    if (iconEl) {
      const loggedInSrc = iconEl.dataset.iconLoggedIn || '';
      const guestSrc = iconEl.dataset.iconGuest || '';
      const nextSrc = rewards.showGuestHint ? guestSrc : loggedInSrc;
      if (nextSrc && iconEl.getAttribute('src') !== nextSrc) {
        iconEl.setAttribute('src', nextSrc);
      }
    }

    const canRenderGuestHint = Boolean(guestHintElement);

    if (guestHintElement) {
      const template = guestHintElement.dataset.guestHintTemplate || '';

      if (guestRewardsElement) {
        if (rewards.showGuestHint) {
          guestHintElement.textContent = decodeHtmlEntities(
            guestHintElement.dataset.rewardGuestLabel || 'Log in & check out for first-order rewards:'
          );
          guestRewardsElement.innerHTML = formatGuestRewardValues(template, rewards);
        } else {
          guestHintElement.textContent = decodeHtmlEntities(
            guestHintElement.dataset.rewardLoggedInLabel || 'Estimated rewards:'
          );
          guestRewardsElement.innerHTML = '';
        }
      } else if (rewards.showGuestHint) {
        guestHintElement.innerHTML = formatGuestHint(template, rewards);
      }

      guestHintElement.hidden = guestRewardsElement ? false : !rewards.showGuestHint;
    }

    if (guestRewardsElement) {
      guestRewardsElement.hidden = !rewards.showGuestHint;
    }

    if (milesElement) {
      if (rewards.hasMiles) {
        milesElement.textContent = `${rewards.miles} Miles`;
        milesElement.hidden = false;
      } else {
        milesElement.textContent = '';
        milesElement.hidden = true;
      }
    }

    if (raysElement) {
      if (rewards.hasRays) {
        raysElement.textContent = String(rewards.rays);
        raysElement.hidden = false;
      } else {
        raysElement.textContent = '';
        raysElement.hidden = true;
      }
    }

    if (raysSuffixElement) {
      raysSuffixElement.hidden = !rewards.hasRays;
    }

    if (separatorElement) {
      separatorElement.hidden = !(rewards.hasMiles && rewards.hasRays);
    }

    const hasValue = rewards.hasMiles || rewards.hasRays;

    if (valueElement) {
      // PDP reward box has no guest-hint node; do not hide values there.
      valueElement.hidden = (rewards.showGuestHint && canRenderGuestHint) || !hasValue;
    }

    if (afterPurchaseElement) {
      afterPurchaseElement.hidden = (rewards.showGuestHint && canRenderGuestHint) || !hasValue;
    }

    if (wrapElement) {
      wrapElement.hidden = !hasValue;
    }
  }

  function resolveVariantPrice(variant) {
    if (!variant) return 0;

    try {
      const mpEl = document.querySelector('.product-info__member-discount .js-member-price-data');
      if (mpEl) {
        const mpCfg = JSON.parse(mpEl.textContent);
        const raw = mpCfg.variants[String(variant.id)];
        if (raw !== null && raw !== undefined) {
          const currency = (mpCfg.shopCurrency || window.Shopify?.currency?.active || '').toUpperCase();
          const zeroDec = ['BIF','CLP','DJF','GNF','JPY','KMF','KRW','MGA','PYG','RWF','UGX','VND','VUV','XAF','XOF','XPF'].includes(currency);
          return zeroDec ? Math.round(Number(raw) || 0) : Math.round((Number(raw) || 0) * 100);
        }
      }
    } catch (error) {}

    return Math.floor(Number(variant.price) || 0);
  }

  async function updatePdpRewardBox(box, price, variantId, productId) {
    if (!box) return;

    const rewards = await calculateProductRewards(price, variantId, productId);
    const raysElement = box.querySelector('[data-reward-rays]');

    if (raysElement) {
      raysElement.dataset.basePrice = String(price || 0);
      raysElement.dataset.variantId = String(variantId || '');
    }

    applyRewardDisplay(box, rewards);
  }

  function clearCache() {
    rewardCache.clear();
  }

  return {
    FIRST_ORDER_BONUS,
    isLoggedIn,
    normalizeCartUnitPrice,
    getUnitPriceBase,
    calculateTotalMiles,
    calculateItemMiles,
    calculateItemRays,
    calculateLineRays,
    fetchProductReward,
    fetchProductListReward,
    calculateProductRewards,
    calculateCartRewards,
    applyRewardDisplay,
    resolveVariantPrice,
    updatePdpRewardBox,
    clearCache
  };
})();
